/* oxlint-disable react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md */
import { useEffect } from 'react';
import {
    Navigate,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';

import { getFragment } from '../../graphql';
import {
    type CoverageFlags,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    GetPrescriptionOffersDocument,
    type GetPrescriptionOffersQuery,
    PrescriberOrderStatus,
    type ProviderOffersInfoFragment,
    ProviderOffersSortBy,
} from '../../graphql/generated/graphql';
import { usePatientContext } from '../../hooks/use-patient-context';
import { usePrescriptionSubstitution } from '../../hooks/use-prescription-substitution';
import { usePriorAuthorizationDetails } from '../../hooks/use-prior-authorization-details';
import { QueryLoader } from '../../loaders';
import { getAbsoluteRoute } from '../../util';
import { usePrescriptionInputVariables } from '../cabinet/prescription/use-prescription-variables';

import { OrderDetailsV2 } from './details/OrderDetailsV2';

const OrderDetailsRouteV2 = ({
    prescriberOrderdata,
}: {
    prescriberOrderdata: GetPrescriptionOffersQuery;
}) => {
    let offer;
    let order;

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const contextParam = searchParams.get('context');

    const prescriberOrderFragment = getFragment(
        prescriberOrderdata?.prescriberOrderV2
    );

    if (
        prescriberOrderFragment?.__typename ===
        'DigitallyReceivedPrescriberOrder'
    ) {
        order = getFragment(
            prescriberOrderFragment
        ) as DigitallyReceivedPrescriberOrderInfoFragment;

        if (order?.latestSelectedOffer) {
            offer = getFragment(
                order.latestSelectedOffer
            ) as unknown as ProviderOffersInfoFragment;
        }
    }
    if (!order) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const { priorAuthorizationDetails } = usePriorAuthorizationDetails(order);

    // SMS context reroute logic
    useEffect(() => {
        if (!contextParam) return;
        if (priorAuthorizationDetails) {
            navigate(
                getAbsoluteRoute(
                    'priorAuthorization.priorAuthorizationId.status',
                    {
                        priorAuthorizationId: priorAuthorizationDetails.id,
                    }
                )
            );
        } else if (order.status === PrescriberOrderStatus.New) {
            navigate(
                getAbsoluteRoute('cabinet.rxId.root', {
                    prescriptionId: order.drxId,
                })
            );
        } else if (
            order.status === PrescriberOrderStatus.SentToPharmacy ||
            order.status !== PrescriberOrderStatus.Filled ||
            (order.status === PrescriberOrderStatus.Cancelled &&
                order.assignmentTimestamp)
        ) {
            navigate(
                getAbsoluteRoute('orders.prescriberOrderId.confirmation', {
                    prescriberOrderId: order.id,
                })
            );
        }
    }, [contextParam, order, navigate, priorAuthorizationDetails]);

    const { authorizedMedicationConnection } =
        usePrescriptionSubstitution(prescriberOrderdata);

    const coverageFlags = authorizedMedicationConnection?.coverageFlags?.find(
        (flag) => flag.groupCode === 'ROOT'
    ) as CoverageFlags;

    return (
        <OrderDetailsV2
            offer={offer}
            prescriberOrder={order}
            coverageFlags={coverageFlags}
        />
    );
};

const OrderDetailsLoader = () => {
    const { prescriberOrderId } = useParams();
    const { scopedPatientId } = usePatientContext();

    const PrescriptionOffersLoader = QueryLoader<
        typeof GetPrescriptionOffersDocument
    >;

    if (!prescriberOrderId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    // TODO: There's a temporary hard coded distance of 5 in order to get the coverageFlags off offers. We need to add a new endpoint to Medication Marketplace to grab coverage without offers to avoid this.
    const input = usePrescriptionInputVariables(5, scopedPatientId);

    return (
        <PrescriptionOffersLoader
            query={GetPrescriptionOffersDocument}
            variables={{
                prescriberOrderId,
                authorizedMedicationInput: {
                    coordinatesInput: input.coordinatesInput,
                    sortBy: ProviderOffersSortBy.PatientCost,
                },
                prescribedMedicationInput: {
                    coordinatesInput: input.coordinatesInput,
                    sortBy: ProviderOffersSortBy.PatientCost,
                },
            }}
            component={({ data }) => (
                <OrderDetailsRouteV2 prescriberOrderdata={data} />
            )}
        />
    );
};

export default OrderDetailsLoader;
